import { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, CircularProgress, Avatar,
} from '@mui/material';
import {
  Work as PositionsIcon,
  People as CandidatesIcon,
  CheckCircle as ActiveIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

const AtsHomeLayout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, project, projectName, authToken } = useStore.getState();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({ total: 0, active: 0, candidates: 0 });

  const userName = user?.name || user?.displayName || '';

  useEffect(() => {
    const loadMetrics = async () => {
      if (!project) { setLoading(false); return; }
      try {
        setLoading(true);
        const response = await api.get(`/positions`, {
          params: { projectId: project, source: 'ats' },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.data.response?.status_response === 'OK') {
          const positions = response.data.response.data || [];
          setMetrics({
            total: positions.length,
            active: positions.filter((p) => p.status === 'active').length,
            candidates: positions.reduce((sum, p) => sum + (p.totalApplications || 0), 0),
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const metricCards = [
    { key: 'positions', value: metrics.total, label: t('ats.home.metrics.positions'), icon: <PositionsIcon />, color: '#6A5AF9' },
    { key: 'active', value: metrics.active, label: t('ats.home.metrics.activePositions'), icon: <ActiveIcon />, color: '#10B981' },
    { key: 'candidates', value: metrics.candidates, label: t('ats.home.metrics.candidates'), icon: <CandidatesIcon />, color: '#0EA5E9' },
  ];

  const steps = [1, 2, 3, 4, 5].map((n) => t(`ats.home.howItWorks.step${n}`));

  return (
    <Box sx={{ p: 3 }}>
      {/* Bienvenida */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          {userName ? t('ats.home.welcomeName', { name: userName }) : t('ats.home.welcome')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('ats.home.subtitle')}{projectName ? ` — ${projectName}` : ''}
        </Typography>
      </Box>

      {/* Métricas */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {metricCards.map((m) => (
          <Grid item xs={12} sm={4} key={m.key}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: `${m.color}1A`, color: m.color }}>
                  {m.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {loading ? <CircularProgress size={20} /> : m.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Accesos rápidos */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/ats/positions/new')}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
        >
          {t('ats.home.actions.newPosition')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PositionsIcon />}
          onClick={() => navigate('/ats/positions')}
          sx={{ borderColor: '#6A5AF9', color: '#6A5AF9', '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' } }}
        >
          {t('ats.home.actions.viewPositions')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PersonSearchIcon />}
          onClick={() => navigate('/ats/candidates')}
          sx={{ borderColor: '#6A5AF9', color: '#6A5AF9', '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' } }}
        >
          {t('ats.home.actions.viewCandidates')}
        </Button>
      </Box>

      {/* Cómo funciona */}
      <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {t('ats.home.howItWorks.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {steps.map((text, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{
                  width: 26, height: 26, flexShrink: 0,
                  borderRadius: '50%', backgroundColor: '#6A5AF9', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.8rem', fontWeight: 700, mt: 0.2,
                }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ pt: 0.3 }}>{text}</Typography>
              </Box>
            ))}
          </Box>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/ats/positions')}
            sx={{ mt: 2, color: '#6A5AF9' }}
          >
            {t('ats.home.actions.start')}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AtsHomeLayout;
